import {
  PortalSharedDocumentContentResponseSchema,
  PortalSharedDocumentListResponseSchema,
  PortalSharedDocumentReturnCommitResponseSchema,
  PortalSharedDocumentReturnCommitSchema,
  PortalSharedDocumentReturnUploadRequestSchema,
  PortalSharedDocumentReturnUploadTokenResponseSchema,
  type PortalSharedDocument,
  type PortalSharedDocumentContentResponse,
  type PortalSharedDocumentReturnCommitResponse,
} from "@l30/shared";
import { useMutation, useQuery } from "@tanstack/react-query";

import { portalFetch } from "./portal-client.js";

/**
 * Portal shared-document hooks.
 *
 * Two surfaces:
 *   - `useSharedDocuments()` — lists all active shared-document grants
 *     for the portal user's engagement.
 *   - `useSharedDocumentContent()` — fetches a short-lived signed URL
 *     for a given grant so the viewer can render the file. Uses
 *     useMutation (lazy/on-demand) rather than useQuery because it is
 *     invoked per selection, not on page mount.
 *
 * Every response is parsed against the SSOT schema in `@l30/shared`
 * at the fetch boundary — drift surfaces as `ApiContractError`.
 *
 * Canon: docs/superpowers/plans/2026-06-07-portal-external-document-review.md Task 10.
 * Sister hook file: packages/portal-web/src/api/portal-vault.ts.
 */

const SHARED_DOCS_KEY = ["portal", "shared-docs"] as const;

export function useSharedDocuments(enabled = true) {
  return useQuery<{ documents: PortalSharedDocument[] }>({
    queryKey: SHARED_DOCS_KEY,
    queryFn: () =>
      portalFetch("/portal/shared-documents", {}, PortalSharedDocumentListResponseSchema),
    refetchOnWindowFocus: false,
    enabled,
  });
}

/**
 * On-demand signed-URL resolution for a shared document.
 *
 * Implemented as useMutation rather than useQuery so the URL is fetched
 * only when the user selects a document. The signed URL has a 5-minute
 * TTL; the viewer re-triggers the mutation if the iframe fails to load.
 */
export function useSharedDocumentContent() {
  return useMutation<PortalSharedDocumentContentResponse, Error, string>({
    mutationFn: (grantId: string) =>
      portalFetch(
        `/portal/shared-documents/${grantId}/content`,
        {},
        PortalSharedDocumentContentResponseSchema,
      ),
  });
}

/**
 * Counterparty return-file upload for a working-document review grant.
 * Registers a new portal-return version for staff (does not overwrite tip).
 */
export function useSharedDocumentReturnUpload(grantId: string) {
  return useMutation<
    PortalSharedDocumentReturnCommitResponse,
    Error,
    { file: File; note?: string }
  >({
    mutationFn: async ({ file, note }) => {
      const request = PortalSharedDocumentReturnUploadRequestSchema.parse({
        filename: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        ...(note ? { note } : {}),
      });

      const token = await portalFetch(
        `/portal/shared-documents/${grantId}/return-upload-token`,
        {
          method: "POST",
          body: JSON.stringify(request),
        },
        PortalSharedDocumentReturnUploadTokenResponseSchema,
      );

      const upload = await fetch(token.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": request.mimeType,
          "x-ms-blob-type": "BlockBlob",
        },
        body: file,
      });
      if (!upload.ok) {
        throw new Error(`Upload failed: ${upload.status}`);
      }

      const commitBody = PortalSharedDocumentReturnCommitSchema.parse({
        blobKey: token.blobKey,
        filename: request.filename,
        mimeType: request.mimeType,
        sizeBytes: request.sizeBytes,
        ...(note ? { note } : {}),
      });

      return portalFetch(
        `/portal/shared-documents/${grantId}/return-commit`,
        {
          method: "POST",
          body: JSON.stringify(commitBody),
        },
        PortalSharedDocumentReturnCommitResponseSchema,
      );
    },
  });
}
